/* eslint-disable @typescript-eslint/no-unused-vars */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';

// Types
import {
	TSelectOption,
	TSortColumn,
} from '@global-types/index';

// Hooks
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import { useElementSize } from '@mantine/hooks';
import dayjs from 'dayjs';
import { TParsedConversionFactor } from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

type ObjWithStartDate = {
	startDate: {
		$gte: string;
		$lt: string;
	};
};

/**
 * Custom hook for managing conversion factor list functionality.
 * @returns {Array} An array containing references and callback functions.
 */
export const useConversionFactor = () => {
	const [queryParamsProducers, setQueryParamsProducers] =
		useState({ entity: [] });
	const [queryParamsProjects, setQueryParamsProjects] =
		useState({});
	const [resetFilterSites, setResetFilterSites] =
		useState(false);
	const [resetFilterProjects, setResetFilterProjects] =
		useState(false);
	const { checkAcl, checkAccess } = useAuthorization();
	const [isOverflowFilters] = useState(false);

	const [openDrawer, setOpenDrawer] = useState(false);

	const [
		openDrawerAddConversionFactor,
		setOpenDrawerAddConversionFactor,
	] = useState(false);

	const [
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
	] = useState(false);

	const [
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
	] = useState(false);

	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const {
		conversionFactorsPage,
		conversionFactorsPageSize,
		setConversionFactorsPage,
		conversionFactorsSelectedRecords,
		setConversionFactorsSelectedRecords,
		conversionFactorsOpenConfirmationModalDelete,
		setConversionFactorsOpenConfirmationModalDelete,
		conversionFactorsQueryParams,
		setConversionFactorsQueryParams,
		conversionFactorsSortStatus,
		setConversionFactorsSortStatus,
		setConversionFactorsSortParams,
		conversionFactorsSortParams,
		setConversionFactorsPageSize,
		setConversionFactorsSelectedRow,
		conversionFactorsSelectedRow,
		setIsOpenStartMint,
		conversionFactorsDatatableOptions,
		setConversionFactorsDatatableOptions,
	} = useStore();

	const containerRef = useRef<HTMLDivElement>(null);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const handleSortParams = (_column: TSortColumn) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'conversionFactor':
				sortParams = 'conversionFactor';
				break;
			case 'mintStatus':
				sortParams = 'mintStatus';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setConversionFactorsSortParams(sortParams);
		} else {
			setConversionFactorsSortParams('-' + sortParams);
		}
	};

	const handleStartMint = () => {
		//setConversionFactorsStartMintsActiveStep(0);
		setIsOpenStartMint(true);
	};

	const handleReset = () => {
		setConversionFactorsPageSize(100);
		setConversionFactorsQueryParams({});
		setConversionFactorsPage(1);
		setConversionFactorsSortParams('-createdAt');
		setConversionFactorsSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function hasStartDate(obj: ObjWithStartDate) {
		if (
			Object.keys(obj).length === 0 &&
			obj.constructor === Object
		) {
			return false;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte !== undefined &&
			obj.startDate.$lt !== undefined
		) {
			return true;
		}

		if (
			obj.startDate &&
			obj.startDate.$gte === undefined &&
			obj.startDate.$lt === undefined
		) {
			return false;
		}

		return false;
	}

	const NBR_SELECTED_ROWS =
		conversionFactorsSelectedRecords.length;

	const selectedFields = [
		'_id',
		'project.name',
		'site.name',
		'startDate',
		'quantity',
		'verified',
	];

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Pagination ****************************/

	const handlePaginationChangeSize = function (
		_size: string | number
	) {
		setConversionFactorsPageSize(Number(_size));
		setConversionFactorsPage(1);
	};

	const handleChangePagination = (data: number) => {
		setConversionFactorsPage(data);
	};

	/**************************** Filter Projects ****************************/

	const handleapplyprojectfilter = (
		projects: TSelectOption[]
	) => {
		setConversionFactorsSelectedRecords([]);
		setConversionFactorsPage(1);
		const queryParamsProjects = {
			project: projects.map(
				(_project: TSelectOption) => _project.value
			),
			site: [],
		};
		setQueryParamsProjects(queryParamsProjects);
		setConversionFactorsQueryParams({
			...conversionFactorsQueryParams,
			...queryParamsProjects,
		});
		setResetFilterSites(true);
	};

	/**************************** Filter Date ****************************/
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleapplydatefilter = (date: any) => {
		setConversionFactorsSelectedRecords([]);
		let startDate;
		let endDate;
		if (
			dayjs(date[0].value[0]).isValid() &&
			dayjs(date[0].value[1]).isValid()
		) {
			startDate = date[0].value[0];
			endDate = date[0].value[1];
		} else {
			startDate = dayjs()
				.subtract(364, 'days')
				.toISOString();
			endDate = dayjs().toISOString();
		}

		const queryParamsDate = {
			startDate: {
				$gte: startDate,
				$lt: endDate,
			},
		};

		setConversionFactorsQueryParams({
			...conversionFactorsQueryParams,
			...queryParamsDate,
		});
		setConversionFactorsPage(1);
	};
	/**************************** Filter Sites ****************************/

	const handleapplysitefilter = (
		sites: TSelectOption[]
	) => {
		setConversionFactorsSelectedRecords([]);
		setConversionFactorsPage(1);
		const queryParamsSites = {
			site: sites.map(
				(_site: TSelectOption) => _site.value
			),
		};

		setConversionFactorsQueryParams({
			...conversionFactorsQueryParams,
			...queryParamsSites,
		});
	};

	/**************************** Filter Producers ****************************/

	const handleapplyproducerfilter = (
		producers: TSelectOption[]
	) => {
		setConversionFactorsSelectedRecords([]);
		setConversionFactorsPage(1);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const queryParamsProducers: any = {
			entity: producers.map(
				(_producer: TSelectOption) => _producer.value
			),
			site: [],
			project: [],
		};
		setQueryParamsProducers(queryParamsProducers);

		setConversionFactorsQueryParams({
			...conversionFactorsQueryParams,
			...queryParamsProducers,
		});
		setResetFilterProjects(true);
		setResetFilterSites(true);
	};
	/**************************** Sort ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlechangesortstatus = (data: any) => {
		handleSortParams(data);
		setConversionFactorsSortStatus(data);
	};

	const handleselectedrecordchange = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		selectedRecords: any
	) => {
		setConversionFactorsSelectedRecords(selectedRecords);
	};

	/**************************** Datatable ****************************/

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlerowclicked = (row: any) => {
		if (
			checkAcl(ACLS.CONVERSION_FACTORS.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				],
			})
		) {
			setOpenDrawer(true);
			setConversionFactorsSelectedRow(row.record);
		}
	};

	/**************************** Show more ****************************/
	const extractSelectedRow = (
		row: TParsedConversionFactor
	): Partial<TParsedConversionFactor> => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const {
			unit,
			updatedAt,
			createdAt,
			endDate,
			...rest
		} = row;
		return { ...rest };
	};

	const handlerowviewmore = (
		row: TParsedConversionFactor
	) => {
		const selectedRow: Partial<TParsedConversionFactor> =
			extractSelectedRow(row);
		setConversionFactorsSelectedRow(selectedRow);
		setOpenDrawer(true);
	};

	const handleCloseDrawerShowMoreConversionFactor =
		() => {
			setOpenDrawer(false);
		};

	/**************************** Delete confirmation modal ****************************/

	const handlerowremoveitem = (
		row: TParsedConversionFactor
	) => {
		setConversionFactorsOpenConfirmationModalDelete({
			open: true,
			params: row,
		});
	};

	const handledeleteconfirmationmodalconfirm = (
		remove: any
	) => {
		setConversionFactorsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
		remove.mutate(
			String(
				conversionFactorsOpenConfirmationModalDelete
					.params.id
			)
		);
		setOpenDrawer(false);
	};

	const handleclosedeleteconfirmationmodal = () => {
		setConversionFactorsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	/**************************** Delete confirmation modal ****************************/

	const handledeleteconversionfactor = () => {
		setConversionFactorsOpenConfirmationModalDelete({
			open: true,
			params: conversionFactorsSelectedRow,
		});
	};

	/**************************** Drawer add conversion factor ****************************/

	const handleCloseDrawerAddConversionFactor = () => {
		setOpenDrawerAddConversionFactor(false);
	};

	/**************************** Delete Multiple selection modal ****************************/

	const handledeletemultipleselectionconfirm = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		removeMultiple: any
	) => {
		const idsList = [...conversionFactorsSelectedRecords]
			.filter((item1) => !item1.isUsed)
			.map((item2) => item2.id);
		removeMultiple.mutate({
			ids: idsList,
		});

		setConversionFactorsSelectedRecords([]);
		setOpenSelectedItemsToDeleteModal(false);
	};

	const handleclosedeletemultipleselectionmodal = () => {
		setOpenSelectedItemsToDeleteModal(false);
	};

	const handleremovedeletemultipleselection = (
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		item: any
	) => {
		const newList = [
			...conversionFactorsSelectedRecords,
		].filter((production) => production.id !== item.id);

		setConversionFactorsSelectedRecords(newList);
	};

	/********************************************************************************************/

	return {
		/**************************** Delete Multiple selection modal ****************************/
		handledeletemultipleselectionconfirm,
		handleclosedeletemultipleselectionmodal,
		handleremovedeletemultipleselection,
		/**************************** Drawer add conversion factor ****************************/
		handleCloseDrawerAddConversionFactor,
		/**************************** Delete item ****************************/
		handlerowremoveitem,
		handledeleteconfirmationmodalconfirm,
		handleclosedeleteconfirmationmodal,
		handledeleteconversionfactor,
		/**************************** Show more ****************************/
		handlerowviewmore,
		handleCloseDrawerShowMoreConversionFactor,
		/**************************** Datatable ****************************/
		handlerowclicked,
		/**************************** Select item ****************************/
		handleselectedrecordchange,
		/**************************** Sort ****************************/
		handlechangesortstatus,
		/**************************** Filters ****************************/
		handleapplyprojectfilter,
		handleapplysitefilter,
		handleapplydatefilter,
		handleapplyproducerfilter,
		/**************************** Pagination ****************************/
		handleChangePagination,
		handlePaginationChangeSize,
		conversionFactorsPage,
		conversionFactorsPageSize,

		setConversionFactorsPage,
		conversionFactorsSelectedRecords,
		setConversionFactorsSelectedRecords,
		conversionFactorsOpenConfirmationModalDelete,
		setConversionFactorsOpenConfirmationModalDelete,
		conversionFactorsQueryParams,
		setConversionFactorsQueryParams,
		conversionFactorsSortStatus,
		setConversionFactorsSortStatus,
		setConversionFactorsSortParams,
		conversionFactorsSortParams,
		setConversionFactorsPageSize,
		setConversionFactorsSelectedRow,
		conversionFactorsSelectedRow,
		setIsOpenStartMint,
		queryParamsProducers,
		setQueryParamsProducers,
		queryParamsProjects,
		setQueryParamsProjects,
		handleScroll,
		resetFilterSites,
		setResetFilterSites,
		isOverflowFilters,
		openDrawer,
		setOpenDrawer,
		openSelectedItemsToDownloadModal,
		setOpenSelectedItemsToDownloadModal,
		openSelectedItemsToVerifyModal,
		setOpenSelectedItemsToVerifyModal,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddConversionFactor,
		setOpenDrawerAddConversionFactor,
		handleSortParams,
		handleStartMint,
		hasStartDate,
		NBR_SELECTED_ROWS,
		selectedFields,
		ref,
		containerRef,
		resetFilterProjects,
		setResetFilterProjects,
		conversionFactorsDatatableOptions,
		setConversionFactorsDatatableOptions,
	};
};
